import { format } from 'date-fns';
import * as React from 'react';

import { Button } from '@/components/core/button';
import { Calendar } from '@/components/core/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/core/popover';
import { DEFAULT_DATE_FORMAT } from '@/constants';
import { cn } from '@/shared/helpers';

type DatePickerProps = {
  selected?: Date;
  onSelect?: (date?: Date) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
};

export function DatePicker({
  selected,
  onSelect,
  disabled = false,
  placeholder = 'Pick a date',
  className,
  buttonClassName,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) {
      return;
    }
    setOpen(nextOpen);
  };

  const handleSelect = (date?: Date) => {
    onSelect?.(date);
    if (date) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-empty={!selected}
          className={cn(
            'w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground',
            buttonClassName,
          )}
        >
          {selected ? format(selected, DEFAULT_DATE_FORMAT) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-auto p-0', className)}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
